import {React, useState} from 'react';
import '../../App.css';
import { useNavigate } from 'react-router-dom';
import {Grid, Box, Button, Paper, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography} from '@mui/material';
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import * as monthList from '../../services/monthList';
import * as yearList from '../../services/yearList';
import Popup from '../Popup';
import PFReportTable from '../table-data/PFReportTable';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Notification from '../Notification';
import { SPLIT_MONTH } from '../generated/PFSplitECR';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red' , marginLeft: '3px'}}>*</span>
  </span>
);

const monthOptions = monthList.getMonthList();
const yearOptions = yearList.getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues={
  id: 0,
  month: currentMonthName,
  year: currentYear,
}

function ProvidentFundReport(){

  const [empDetails, setEmpDetails] = useState([]);
  const [notify, setNotify] = useState({isOpen: false, message: '', type: ''});
  const navigate = useNavigate();

  const validate = () => {
    let temp = {}
    temp.month = values.month.length != 0 ? "" : "This field is required."
    temp.year = values.year.length != 0 ? "" : "This field is required."

    setErrors({
      ...temp
    })

    return Object.values(temp).every(x => x == "")
  }

  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [openPopup, setOpenPopup] = useState(false);

  function handleInputChange(e){
    const {name, value} = e.target;
    setValues({
      ...values,
      [name]: value
    })
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    // Temporary: September 2026 opens the split-month ECR page. Remove with PFSplitECR.jsx.
    if (validate() && values.month === SPLIT_MONTH.month && String(values.year) === SPLIT_MONTH.year) {
      Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/report/pf-report-details`, {
          params: { month: values.month, year: values.year }
        }),
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/report/emp-details`, values)
      ])
        .then(([pfResponse, empResponse]) => {
          navigate('/pf-split-ecr', {
            state: { empDetails: empResponse.data, pfDetail: pfResponse.data?.[0] || null }
          });
        })
        .catch((e) => {
          setNotify({
            isOpen: true,
            message: e.response?.status === 401 ? 'Session Expired!' : 'Could not load payroll for September 2026.',
            type: 'error'
          });
          if (e.response?.status === 401) setTimeout(() => navigate('/'), 1000);
        });
      return;
    }

    if(validate()){
      Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/report/pf-report-details`, {
          params: { month: values.month, year: values.year }
        }),
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/report/emp-details`, values)
      ])
      .then(([pfResponse, empResponse]) => {
        const pfDetail = pfResponse.data[0];
        if (!pfDetail) {
          setNotify({
            isOpen: true,
            message: `No PF rule is effective for ${values.month} ${values.year}. Please configure PF Rules first.`,
            type: 'error'
          });
          return;
        }
        setEmpDetails(empResponse.data);
        navigate('/pf-report', {state:{empDetails:empResponse.data, pfDetail:pfDetail, month: values.month, year: values.year}});
      })
      .catch((e)=>{
        if(e.response?.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          },1000);
        }
      })
    }
  }

  return(
    <Box sx={{display: "flex" ,justifyContent: "center", margin: "10px", padding: "50px", fontWeight: "500px"}}>
      <Paper elevation={5} sx={{width: "800px",height: "210px"}}>
        <Grid className="form-title" container spacing={2} justifyContent="center"style={{marginTop: "15px"}} alignItems="center">
          <Grid item> <HiOutlineCurrencyRupee size={30}  sx={{width: 45, height: 45}}/> </Grid>
          <Grid item> <Typography variant="h5">Provident Fund Report</Typography> </Grid>
        </Grid>
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>

          <Box sx={{margin: "30px"}}>
            {/* All elements in one line */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>
              
              {/* Month field */}
              <FormControl
                variant="outlined"
                sx={{ width: '200px' }}
                {...(errors.month && {error:true} )}
              >
                <InputLabel><RequiredLabel label="Month" /></InputLabel>
                <Select
                  label="Month"
                  name="month"
                  size="small"
                  value={values.month}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    monthOptions.map(
                      item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                    )
                  }
                </Select>
                { errors.month && <FormHelperText>{errors.month}</FormHelperText>}
              </FormControl>

              {/* Year field */}
              <FormControl
                variant="outlined"
                sx={{ width: '200px' }}
                {...(errors.year && {error:true} )}
              >
                <InputLabel><RequiredLabel label="Year" /></InputLabel>
                <Select
                  label="Year"
                  name="year"
                  size="small"
                  value={values.year}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    yearOptions.map(
                      item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                    )
                  }
                </Select>
                { errors.year && <FormHelperText>{errors.year}</FormHelperText>}
              </FormControl>

              {/* Display button */}
              <Button 
                className="form-btn" 
                color="success" 
                variant="contained" 
                type="submit" 
                onClick={handleSubmit}
                sx={{ height: 40 }}
              >
                Display
              </Button>
            {/* <Link to='/ecr-text-file' className='link' state={{empDetails:empDetails, pfDetail:pfDetail}}>
              <Button variant="outlined" color="secondary">Generate ECR</Button>
            </Link> */}
          
            </Box>
          </Box>
        </form>

        

        <Popup
          title="Employee Form"
          openPopup={openPopup}
          setOpenPopup={setOpenPopup}
        >
          <PFReportTable/>
        </Popup>

      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify} 
      />
    </Box>
    // <div>test</div>
  )
}

export default ProvidentFundReport;